import { Box, Button, CircularProgress, LinearProgress, Typography, useTheme } from '@mui/material';
import { FC } from 'react';
import { ExportActions, ExportState } from '../hooks/useExportManager';

interface ExportButtonProps {
  exportState: ExportState;
  exportActions: ExportActions;
}

function isExportJobActive(status: ExportState['exportJobStatus']): boolean {
  return status !== null && status !== 'completed' && status !== 'failed';
}

function getProgressPercentage(totalImages: number, processedImages: number): number {
  return totalImages > 0 ? Math.round((processedImages / totalImages) * 100) : 0;
}

const ExportButton: FC<ExportButtonProps> = ({ exportState, exportActions }) => {
  const theme = useTheme();

  const hasSelection = exportState.selectedEventIds.length > 0;
  const isActive = isExportJobActive(exportState.exportJobStatus);
  const isDisabled = !hasSelection || exportState.exportInProgress;

  function renderButtonContent() {
    if (exportState.exportInProgress && !exportState.exportJobStatus) {
      return (
        <>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          Skapar exportjobb...
        </>
      );
    }

    if (exportState.exportJobStatus === 'pending') {
      return (
        <>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          Förbereder export...
        </>
      );
    }

    if (exportState.exportJobStatus === 'in-progress-gathering-metadata') {
      return (
        <>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          Samlar metadata...
        </>
      );
    }

    if (exportState.exportJobStatus === 'in-progress-making-zip') {
      const progress = getProgressPercentage(
        exportState.exportJobTotalImages,
        exportState.exportJobProcessedImages
      );
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <CircularProgress size={20} />
          <Box sx={{ minWidth: 35 }}>
            <Typography variant="body2" color="text.secondary">
              {progress}%
            </Typography>
          </Box>
          <Box sx={{ width: '100%', mr: 1 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Skapar zip...
          </Typography>
        </Box>
      );
    }

    if (exportState.exportJobStatus === 'in-progress-storing-zip') {
      return (
        <>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          Snart klart...
        </>
      );
    }

    if (exportState.exportJobStatus === 'completed') {
      return <>Exportering klar!</>;
    }

    if (exportState.exportError) {
      return <>Exportering misslyckades</>;
    }

    const count = exportState.selectedEventIds.length;
    if (count > 0) {
      return <>{`Exportera ${count} album`}</>;
    }

    return <>Exportera album</>;
  }

  return (
    <Button
      variant="outlined"
      disabled={isDisabled}
      onClick={exportActions.startExport}
      sx={{
        color: theme.palette.text.primary,
        borderColor: theme.palette.text.primary,
        minWidth: isActive ? 400 : undefined,
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
          borderColor: theme.palette.text.primary,
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.action.disabled,
        },
      }}
    >
      {renderButtonContent()}
    </Button>
  );
};

export default ExportButton;
